/**
 * Queries objetivo de SEO.
 *
 * Cada query abre **Search Console con el filtro de esa query ya aplicado**,
 * nunca la home del informe: el texto de la query es su clave natural en Search
 * Console y el builder del contrato la codifica con el prefijo de coincidencia
 * exacta. Si falta el `resourceId` de la propiedad, el builder devuelve `null` y
 * la fila muestra "sin vincular" en vez de un link a una pantalla vacía.
 *
 * El semáforo de la fila es **la página en la que cae el resultado** —hasta la
 * posición 10 primera página, hasta 20 segunda—, y esa regla se publica en la
 * bajada de la sección para que el color sea auditable. La posición misma va en la
 * celda al lado de la palabra: nunca color solo.
 */
import {
  SEO_ACCION_LABELS,
  type Links,
  type SeoQuery,
} from '@ops/contracts'

import { useDeepLinks } from '../../lib/deeplinks'
import { formatMetricValue } from '../../lib/format'
import { useVincular } from '../../lib/vincular'
import {
  type Column,
  DataTable,
  ExternalLink,
  HealthDot,
  MissingCell,
  Panel,
  ProvenanceBadge,
  StatusPill,
} from '../../ui'
import { etiquetaSeo, PRIORIDAD_LABEL, severidadSeo } from './derivar'

export interface SeoBlockProps {
  queries: readonly SeoQuery[]
  /** Sobre de deep links del payload, indexado por el texto de la query. */
  links: Links['seoQueries']
  now: Date
}

/**
 * Una celda numérica que puede faltar.
 *
 * Un guion pelado no distingue "no aplica" de "nadie lo cargó", y las dos
 * lecturas mandan a hacer cosas distintas: la primera se ignora, la segunda se
 * arregla. Es la regla 2 del panel llevada al tamaño de una fila.
 *
 * Por qué sin botón de carga: hoy **no hay endpoint que escriba estos campos**.
 * Clics, impresiones y CTR los reporta Search Console —y el nombre de la query,
 * en esta misma fila, abre su informe filtrado—; volumen y dificultad son
 * research manual y el endpoint de ingesta escribe métricas del catálogo por
 * slug, no columnas de una query. La acción que existe de verdad ya está en la
 * fila: el link a Search Console y el badge de Fuente, que lleva a Admin. Un
 * tercer botón que no pueda terminar lo que promete sería peor que el estado.
 */
function celda(
  valor: number | null,
  que: string,
  unidad: 'count' | 'percent',
) {
  return valor === null ? (
    <MissingCell que={que} />
  ) : (
    formatMetricValue(valor, unidad)
  )
}

export function SeoBlock({ queries, links, now }: SeoBlockProps) {
  const builders = useDeepLinks()
  const vincular = useVincular()

  /**
   * El sobre del servidor manda. Solo cuando la query **no está** en el sobre
   * (no cuando su link es `null`) se arma en el front con la config de
   * `/api/config`: un `null` del servidor es una decisión, un `undefined` es una
   * query que ese endpoint no acompañó.
   */
  const linkDe = (query: string) => {
    const sobre = links[query]
    return sobre === undefined
      ? builders.searchConsoleQuery(query)
      : sobre.searchConsole
  }

  const columnas: ReadonlyArray<Column<SeoQuery>> = [
    {
      key: 'query',
      header: 'Query',
      rowHeader: true,
      sortValue: (q) => q.query,
      filterValue: (q) => `${q.query} ${SEO_ACCION_LABELS[q.accion]}`,
      render: (q) => {
        const link = linkDe(q.query)
        if (link !== null) {
          return (
            <ExternalLink link={link} variante="texto">
              {q.query}
            </ExternalLink>
          )
        }
        return (
          <div className="min-w-0">
            <span className="block truncate">{q.query}</span>
            <ExternalLink
              link={null}
              sistemaFaltante="Search Console"
              onVincular={() =>
                vincular(`la query «${q.query}» en Search Console`)
              }
            />
          </div>
        )
      },
    },
    {
      key: 'posicion',
      header: 'Posición',
      width: '190px',
      sortValue: (q) => q.posicion,
      render: (q) => (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {/* Regla 2 también acá, y es la celda que más importa: la posición
              gobierna el semáforo de toda la fila, así que un guion pelado deja
              el color sin base a la vista. */}
          {q.posicion === null ? (
            <MissingCell que="posición" />
          ) : (
            <span className="tabular text-base font-semibold text-text">
              {formatMetricValue(q.posicion, 'position')}
            </span>
          )}
          {/* La lectura del color, en palabras y al lado de su base. */}
          <HealthDot
            health={severidadSeo(q)}
            label={etiquetaSeo(q)}
            size="sm"
          />
        </div>
      ),
    },
    {
      key: 'clics',
      header: 'Clics',
      align: 'right',
      width: '130px',
      sortValue: (q) => q.clics,
      render: (q) => celda(q.clics, 'clics', 'count'),
    },
    {
      key: 'impresiones',
      header: 'Impresiones',
      align: 'right',
      width: '150px',
      sortValue: (q) => q.impresiones,
      render: (q) => celda(q.impresiones, 'impresiones', 'count'),
    },
    {
      key: 'ctr',
      header: 'CTR',
      align: 'right',
      width: '130px',
      sortValue: (q) => q.ctrPct,
      // La base del CTR son los clics y las impresiones, y las dos están en la
      // misma fila: el porcentaje se puede recalcular a mano.
      render: (q) => celda(q.ctrPct, 'CTR', 'percent'),
    },
    {
      key: 'volumen',
      header: 'Volumen',
      align: 'right',
      width: '140px',
      sortValue: (q) => q.volumen,
      render: (q) => celda(q.volumen, 'volumen', 'count'),
    },
    {
      key: 'dificultad',
      header: 'Dificultad',
      align: 'right',
      width: '140px',
      sortValue: (q) => q.dificultad,
      render: (q) => celda(q.dificultad, 'dificultad', 'count'),
    },
    {
      key: 'accion',
      header: 'Acción',
      width: '140px',
      sortValue: (q) => SEO_ACCION_LABELS[q.accion],
      render: (q) => (
        <StatusPill severidad="unknown" size="sm" icono={false}>
          {SEO_ACCION_LABELS[q.accion]}
        </StatusPill>
      ),
    },
    {
      key: 'prioridad',
      header: 'Prioridad',
      width: '90px',
      sortValue: (q) => PRIORIDAD_LABEL[q.prioridad],
      render: (q) => PRIORIDAD_LABEL[q.prioridad],
    },
    {
      key: 'fuente',
      header: 'Fuente',
      width: '200px',
      render: (q) => (
        <ProvenanceBadge
          provenance={q.provenance}
          now={now}
          variant="inline"
          hrefAdmin="/admin"
        />
      ),
    },
  ]

  return (
    <Panel
      titulo="Queries objetivo"
      subtitulo="El nombre de la query abre su informe en Search Console, ya filtrado por esa búsqueda."
      padding="none"
    >
      <DataTable
        columns={columnas}
        rows={queries}
        rowKey={(q) => q.query}
        rowSeverity={severidadSeo}
        densidadPorSeveridad
        filtroPlaceholder="query o acción"
        emptyMessage="No hay queries objetivo cargadas para este período"
        caption={
          <>
            Queries objetivo del período. Posición, clics e impresiones vienen de
            Search Console; volumen y dificultad son research manual.
          </>
        }
      />
    </Panel>
  )
}
